import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { SortOption } from '../../../models';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sort-dropdown',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, SelectModule],
  templateUrl: './sort-dropdown.component.html',
  styleUrls: ['./sort-dropdown.component.scss'],
})
export class SortDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input() options: SortOption[] = [];
  @Input() control?: FormControl;
  @Input() selectedOption?: string;
  @Input() placeholder: string = 'Sort By:';
  @Output() sortChange = new EventEmitter<string>();

  selectedOptionObject: SortOption | null = null;
  isMobile = false;
  private internalControl: FormControl = new FormControl('');
  private destroy$ = new Subject<void>();

  @ViewChild('sortSelect') sortSelect: any;

  ngOnInit(): void {
    this.checkScreen();
    this.initializeControl();
    this.updateSelectedOptionObject();

    // Listen to form control changes
    const activeControl = this.getActiveControl();
    if (activeControl) {
      activeControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateSelectedOptionObject();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  checkScreen() {
    this.isMobile = window.innerWidth < 800; // mobile breakpoint (includes iPad Pro)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.options.length > 0) {
      this.updateSelectedOptionObject();
    }
    if (changes['selectedOption']) {
      this.initializeControl();
      this.updateSelectedOptionObject();
    }
  }

  private initializeControl(): void {
    if (this.control) {
      // Use provided FormControl
      if (this.selectedOption) {
        this.control.setValue(this.selectedOption);
      }
      return;
    } else {
      // Ensure internal control is initialized
      if (!this.internalControl) {
        this.internalControl = new FormControl('');
      }
      if (this.selectedOption) {
        // Use internal control with selectedOption value
        this.internalControl.setValue(this.selectedOption);
      }
    }
  }

  getActiveControl(): FormControl {
    if (this.control) {
      return this.control;
    }
    // Ensure internalControl is always initialized
    if (!this.internalControl) {
      this.internalControl = new FormControl('');
    }
    return this.internalControl;
  }

  private updateSelectedOptionObject(): void {
    const activeControl = this.getActiveControl();
    if (!activeControl) {
      return;
    }
    const currentValue = activeControl.value;
    const found = this.options.find((option) => option.value === currentValue);
    this.selectedOptionObject = found || null;
  }

  onSortChange(event: any) {
    if (event.value !== undefined) {
      const activeControl = this.getActiveControl();
      if (activeControl) {
        activeControl.setValue(event.value);
        this.sortChange.emit(event.value);
      }
    }
  }

  toggleDropdown() {
    if (this.sortSelect) {
      this.sortSelect.overlayVisible = true; // makes sure overlay opens
      this.sortSelect.show();
    }
  }
}
